import React from 'react';
import { useTooltip, useGlobalState } from '../../hooks';
import { Icon } from '../';
import './Label.css';

/**
 * Renders a styled `<label>` element with optional tooltip and "optional" badge.
 *
 * Label text and tooltip description are resolved in this priority order:
 * 1. Explicit string `label` / `title` props passed by the caller.
 * 2. Automatic fallback from the `displayNames` global state, keyed by `displayNameCode` when the
 *    caller gives one and by `htmlFor` otherwise.
 *    - `label` falls back to `display_name` when `label` is `undefined`, `null`, or `true`.
 *    - `title` falls back to `display_name_description`
 *
 * Pass `label={false}` to explicitly suppress rendering even when a displayNames
 * entry exists for the given `htmlFor` key.
 * Pass `label={true}` to explicitly request the displayNames fallback (useful
 * when a default prop of `false` would otherwise suppress rendering).
 *
 * When `displayNames[htmlFor]?.display_name_instruction` is non-empty, the tooltip
 * will show the description text, a blank line, then the instruction text.
 *
 * @param {object} props
 * @param {string|boolean} [props.label]       - Label text, `true` to use displayNames fallback, `false` to suppress.
 * @param {string} [props.title]               - Tooltip description shown on hover.
 * @param {string} [props.htmlFor]             - Linked input `id`; the displayNames lookup key unless `displayNameCode` is given.
 * @param {string} [props.displayNameCode]     - display_name_code to resolve the text from, when it differs
 *                                               from the field name. A form that keys one field on more than
 *                                               one kind of asset needs this: the field name says which
 *                                               column is being edited, not which asset it belongs to.
 * @param {string} [props.className]           - Additional CSS class names.
 * @param {boolean} [props.disabled]           - Applies disabled styling when true.
 * @param {React.ReactNode} [props.suffix]    - Extra content rendered immediately after the label
 *                                               text, before the "optional" badge. Suppressed along
 *                                               with the label when there is no text to decorate.
 * @param {boolean} [props.isOptional=false]   - Shows an "optional" badge when true.
 * @param {function} [props.onClick]           - Click handler.
 */
export const Label = ({
  label,
  title,
  htmlFor,
  displayNameCode,
  className,
  disabled,
  isOptional = false,
  suffix,
  onClick,
}) => {
  const { showTooltip, hideTooltip } = useTooltip();
  const [displayNames] = useGlobalState('displayNames');
  const displayName = displayNames[displayNameCode ?? htmlFor];
  label = label == null || label === true ? displayName?.display_name : label;
  title = title ?? displayName?.display_name_description;
  const instruction = displayName?.display_name_instruction;
  if (instruction) {
    if (title) {
      const suffix = /[.!?]$/.test(title.trimEnd()) ? '' : '.';
      title = `${title}${suffix}\n${instruction}`;
    } else {
      title = instruction;
    }
  }
  const hasTooltip = !!title;

  if (!label) {
    return;
  }

  const handleClick = e => {
    onClick && onClick(e);
  };

  return (
    <label
      className={'Label' + (className ? ` ${className}` : '') + (disabled ? ` disabled` : '')}
      htmlFor={htmlFor}
      onMouseEnter={() => hasTooltip && showTooltip({ title })}
      onMouseLeave={() => hasTooltip && hideTooltip()}
      onClick={handleClick}
    >
      <span>{label}</span>
      {suffix}
      {hasTooltip && <Icon name="info-small" />}
      {isOptional && (
        <span
          className="is-optional"
          onMouseEnter={() => showTooltip({ title: 'This field is optional to fill in' })}
          onMouseLeave={hideTooltip}
        >
          optional
        </span>
      )}
    </label>
  );
};
